import {
  ACKNOWLEDGE_DISABLED_EMAIL_SETTINGS,
  AGREE_TO_USER_AGREEMENT,
  AUTHENTICATION_SUCCESS,
  CANCEL_CHALLENGE,
  CANCEL_LOGIN,
  IMPERSONATE_USER_FAILURE,
  IMPERSONATE_USER_REQUEST,
  IMPERSONATE_USER_SUCCESS,
  LOGIN_CHALLENGE_COMPLETE,
  LOGIN_USER_FAILURE,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER,
  PASSWORD_RESET_FAILURE,
  PASSWORD_RESET_INITIATE_FAILURE,
  PASSWORD_RESET_INITIATE_SUCCESS,
  PASSWORD_RESET_SUCCESS,
  RECEIVE_AUTH_USER,
  REFRESH_TOKEN_SUCCESS,
  REQUEST_AUTH_TOKEN,
  REQUEST_AUTH_USER,
  SET_CUSTOMER,
  SET_LOGIN_REDIRECT,
  VERIFY_USER_FAILURE,
  VERIFY_USER_SUCCESS,
} from '@sophocles/features/auth/authActions';
import tokenService from '@sophocles/services/token';

const isAuthenticated = () => {
  return tokenService.idToken != null || tokenService.impersonationToken != null;
};

const initialState = {
  loginRedirect: '/',
  pool: tokenService.pool,
  user: tokenService.user,
  username: tokenService.user ? tokenService.user.email : null,
  customer: null,
  token: tokenService.idToken,
  impersonationToken: tokenService.impersonationToken,
  impersonating: {
    user: tokenService.impersonatedUser,
    username: tokenService.impersonatedUser ? tokenService.impersonatedUser.email : null,
  },
  identityProviders: [],
  passwordPolicy: {
    length: 8,
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
  },
  status: {
    isAuthenticating: false,
    isAuthenticated: false,
    isEnabled: true,
    isVerified: isAuthenticated(),
    isLegacyAuth: tokenService.isLegacyAuth,
    isImpersonating: tokenService.isImpersonating,
    isEnrolled: false,
    hasError: false,
    message: null,
    passwordReset: {
      required: false,
      inProgress: false,
      destination: null,
      medium: null,
    },
    challenge: {
      name: null,
      session: null,
      parameters: {
        medium: null,
        destination: null,
      },
    },
  },
};

function getState(state, action) {
  const newState = { ...state };
  switch (action.type) {
    case 'LOGIN_CHALLENGE_COMPLETE':
      newState.status.challenge = initialState.status.challenge;
      return newState;
    case CANCEL_CHALLENGE:
      newState.status.challenge.name = null;
      newState.status.challenge.session = null;
      newState.status.challenge.parameters = { medium: null, destination: null };
      newState.status.passwordReset.inProgress = false;
      newState.status.message = null;
      return newState;
    case CANCEL_LOGIN:
      newState.user = null;
      newState.pool = null;
      newState.identityProviders = [];
      newState.status.isLegacyAuth = false;
      newState.status.isVerified = false;
      newState.status.isAuthenticated = false;
      newState.status.isAuthenticating = false;
      newState.status.message = null;
      newState.status.challenge = initialState.status.challenge;
      newState.status.passwordReset.inProgress = false;
      newState.passwordPolicy = initialState.passwordPolicy;
      tokenService.user = null;
      tokenService.pool = null;
      return newState;
    case LOGIN_USER_REQUEST:
      newState.status.isAuthenticating = true;
      newState.status.hasError = false;
      newState.status.message = null;
      return newState;
    case RECEIVE_AUTH_USER:
      if (tokenService.isImpersonating) {
        newState.impersonating.user = action.user;
        newState.impersonating.username = action.email;
        newState.status.isImpersonating = true;
        newState.status.isAuthenticated = true;
        newState.status.isVerified = true;
        newState.status.hasError = false;
        newState.status.message = null;
        tokenService.impersonatedUser = { id: action.user, email: action.email };
      } else {
        newState.user = action.user;
        newState.impersonating.user = null;
        newState.impersonating.username = null;
        newState.status.isImpersonating = false;
        newState.status.isAuthenticated = true;
        newState.status.isVerified = true;
        newState.status.hasError = false;
        newState.status.message = null;
        tokenService.user = { id: action.user, email: action.email };
      }
      return newState;
    case VERIFY_USER_SUCCESS:
      newState.status.isVerified = true;
      newState.status.isLegacyAuth = action.pool == null;
      newState.status.isEnrolled = action.isEnrolled;
      newState.status.isImpersonating = false;
      newState.impersonating.user = null;
      newState.impersonating.username = null;
      newState.pool = action.pool;
      newState.user = { id: action.id, email: action.username };
      newState.username = action.username;
      newState.identityProviders = action.idps;
      newState.passwordPolicy = action.passwordPolicy || initialState.passwordPolicy;
      tokenService.pool = newState.pool;
      tokenService.isLegacyAuth = newState.status.isLegacyAuth;
      tokenService.isImpersonating = false;
      tokenService.impersonatedUser = null;
      tokenService.user = { id: action.id, email: action.username };
      return newState;
    case VERIFY_USER_FAILURE:
      newState.status.isVerified = false;
      newState.status.isLegacyAuth = false;
      newState.status.isImpersonating = false;
      newState.status.hasError = true;
      newState.status.message = action.err.message;
      newState.pool = null;
      newState.username = null;
      newState.impersonating.user = null;
      newState.impersonating.username = null;
      newState.impersonationToken = null;
      tokenService.pool = null;
      tokenService.user = null;
      tokenService.isLegacyAuth = null;
      tokenService.isImpersonating = false;
      tokenService.impersonatedUser = null;
      tokenService.impersonationToken = null;
      return newState;
    case LOGIN_USER_SUCCESS:
      newState.status.hasError = false;
      newState.status.isVerified = true;
      newState.status.message = null;
      if (action.challenge) {
        newState.status.challenge = action.challenge;
      }
      if (action.tokens) {
        newState.token = action.tokens.id_token;
        tokenService.parseTokens(action.tokens);
      }
      newState.status.isImpersonating = false;
      newState.impersonating.user = null;
      newState.impersonating.username = null;
      newState.impersonationToken = null;
      tokenService.isImpersonating = false;
      tokenService.impersonatedUser = null;
      tokenService.impersonationToken = null;
      return newState;
    case AUTHENTICATION_SUCCESS:
      newState.status.isAuthenticated = true;
      newState.status.isAuthenticating = false;
      return newState;
    case LOGIN_USER_FAILURE:
      newState.token = null;
      newState.username = null;
      newState.user = null;
      newState.status.isImpersonating = false;
      newState.impersonating.user = null;
      newState.impersonating.username = null;
      tokenService.isImpersonating = false;
      tokenService.impersonatedUser = null;
      newState.status.hasError = true;
      newState.status.isAuthenticated = false;
      newState.status.message = action.err.message;
      newState.status.passwordReset = initialState.status.passwordReset;
      newState.status.challenge = initialState.status.challenge;
      newState.status.isEnabled = !action.err.rateLimited;
      return newState;
    case REFRESH_TOKEN_SUCCESS:
      newState.token = action.idToken;
      newState.isAuthenticated = true;
      newState.message = null;
      tokenService.idToken = action.idToken;
      tokenService.accessToken = action.accessToken;
      return newState;
    case PASSWORD_RESET_INITIATE_SUCCESS:
      newState.status.message = null;
      newState.status.challenge.name = action.required ? 'RESET_REQUIRED' : 'FORGOT_PASSWORD';
      newState.status.challenge.session = null;
      newState.status.challenge.parameters.medium = action.codeDelivery.medium;
      newState.status.challenge.parameters.destination = action.codeDelivery.destination;
      newState.status.passwordReset.required = action.required;
      newState.status.passwordReset.inProgress = true;
      return newState;
    case PASSWORD_RESET_INITIATE_FAILURE:
      newState.status.challenge = initialState.status.challenge;
      newState.status.passwordReset = initialState.status.passwordReset;
      newState.status.isEnabled = !action.err.rateLimited;
      newState.status.message = action.err.message;
      newState.status.hasError = true;
      return newState;
    case PASSWORD_RESET_SUCCESS:
      newState.status.passwordReset = initialState.status.passwordReset;
      newState.status.message = null;
      newState.status.hasError = false;
      return newState;
    case PASSWORD_RESET_FAILURE:
      newState.status.isEnabled = !action.err.rateLimited;
      newState.status.message = action.err.message;
      newState.status.hasError = true;
      return newState;
    case LOGOUT_USER:
      newState.status.isAuthenticated = false;
      newState.status.hasError = false;
      newState.token = null;
      newState.impersonationToken = null;
      newState.username = null;
      newState.user = null;
      tokenService.user = null;
      tokenService.clearTokens();
      return newState;
    case SET_LOGIN_REDIRECT:
      newState.loginRedirect = action.path;
      return newState;
    case SET_CUSTOMER:
      newState.customer = action.customer;
      return newState;
    case IMPERSONATE_USER_SUCCESS:
      newState.status.isImpersonating = true;
      newState.impersonationToken = action.token;
      newState.impersonating.user = {
        id: action.userId,
        username: action.email,
        first_name: action.firstName,
        last_name: action.lastName,
      };
      tokenService.isImpersonating = true;
      tokenService.impersonationToken = action.token;
      tokenService.impersonatedUser = { id: action.userId, email: action.email };
      return newState;
    case IMPERSONATE_USER_FAILURE:
      newState.status.isImpersonating = false;
      newState.impersonationToken = null;
      tokenService.isImpersonating = false;
      tokenService.impersonationToken = null;
      tokenService.impersonatedUser = null;
      return newState;
    case REQUEST_AUTH_USER:
    case REQUEST_AUTH_TOKEN:
    case AGREE_TO_USER_AGREEMENT:
    case ACKNOWLEDGE_DISABLED_EMAIL_SETTINGS:
    default:
      return state;
  }
}

function authReducer(state = initialState, action) {
  return getState(state, action);
}

export default authReducer;
